import { useEffect, useMemo, useState } from "react";

import { MAX_FILTERS_QUANTITY } from "app/pages/sales-intelligence/modules/crm-enrichment/constants";
import { useCrmEnrichmentContext } from "app/pages/sales-intelligence/modules/crm-enrichment/contexts/CrmEnrichmentContext";
import crmEnrichmentFiltersService from "app/pages/sales-intelligence/modules/crm-enrichment/services/filters";
import crmEnrichmentTrackingService from "app/pages/sales-intelligence/modules/crm-enrichment/services/tracking/trackingService";
import type {
    CrmEnrichmentJobFilterDTO,
    CrmEnrichmentJobFilterType,
    Operator,
} from "app/pages/sales-intelligence/modules/crm-enrichment/types/types";
import type { CrmColumnItem } from "app/pages/sales-intelligence/sub-modules/management/types";

const INITIAL_NEXT_ID = 2;
const DEFAULT_SELECTED = true;

type HookParams = {
    columns: readonly CrmColumnItem[];
    initialColumns: readonly CrmColumnItem[];
    initialFilters?: readonly CrmEnrichmentJobFilterDTO[];
};

const useCrmEnrichmentJobFilters = ({ initialFilters, initialColumns, columns }: HookParams) => {
    const { crmProvider } = useCrmEnrichmentContext();

    const [isApplyFiltersSelected, setIsApplyFiltersSelected] = useState<boolean | null>(DEFAULT_SELECTED);

    const [filters, setFilters] = useState<readonly CrmEnrichmentJobFilterType[]>([
        crmEnrichmentFiltersService.getDefaultFilter(),
    ]);
    const [nextId, setNextId] = useState(INITIAL_NEXT_ID);

    const crmFieldOptions = useMemo(() => {
        const filterFields = filters.map(({ field }) => field);
        return columns.map(({ label }) => label).filter((option) => !filterFields.includes(option));
    }, [filters, columns]);

    const setFiltersFromInitial = (
        initialFiltersData: readonly CrmEnrichmentJobFilterDTO[] | undefined,
        initialColumnsData: readonly CrmColumnItem[],
    ) => {
        if (!initialFiltersData) {
            return;
        }

        if (initialFiltersData.length) {
            setFilters(
                initialFiltersData.map((filter, index) => ({
                    ...crmEnrichmentFiltersService.createFilterFromDTO(filter, initialColumnsData),
                    id: index + 1,
                })),
            );

            setNextId(initialFiltersData.length + 1);
            setIsApplyFiltersSelected(true);
        } else {
            setIsApplyFiltersSelected(false);
        }
    };

    useEffect(() => {
        if (columns.length > 0) {
            setFiltersFromInitial(initialFilters, initialColumns);
        }
    }, [initialFilters, columns, initialColumns]);

    const onAddFilter = () => {
        if (filters.length < MAX_FILTERS_QUANTITY) {
            crmEnrichmentTrackingService.filters.trackAdd(filters.length + 1, crmProvider);

            setFilters((prevFilters) => [
                ...prevFilters,
                { ...crmEnrichmentFiltersService.getDefaultFilter(), id: nextId },
            ]);
            setNextId((prevNextId) => prevNextId + 1);
        }
    };

    const onDeleteFilter = (id: number) => {
        crmEnrichmentTrackingService.filters.trackRemove(filters.length - 1, crmProvider);

        setFilters((prevFilters) => prevFilters.filter((filter) => filter.id !== id));
    };

    const onFilterChange = ({
        id,
        key,
        value,
    }: {
        id: number;
        key: keyof CrmEnrichmentJobFilterType;
        value: string;
    }) => {
        if (key === "operator") {
            crmEnrichmentTrackingService.filters.trackSelectOperator(value as Operator, crmProvider);
        }

        setFilters((prevFilters) =>
            prevFilters.map((filter) => (filter.id === id ? { ...filter, [key]: value } : filter)),
        );
    };

    const onCrmFieldFilterChange = ({ id, field }: { id: number; field: string }) => {
        const updatedFieldColumn = columns.find(({ label }) => label === field);

        setFilters((prevFilters) =>
            prevFilters.map((filter): CrmEnrichmentJobFilterType => {
                const defaultFilter = crmEnrichmentFiltersService.getDefaultFilter();

                return filter.id === id
                    ? {
                          ...defaultFilter,
                          id,
                          field,
                          dataType: updatedFieldColumn?.dataType ?? defaultFilter.dataType,
                          pickListItems: Object.values(updatedFieldColumn?.pickListItems ?? []),
                      }
                    : filter;
            }),
        );
    };

    const onResetFilters = () => {
        setFilters([crmEnrichmentFiltersService.getDefaultFilter()]);
        setNextId(INITIAL_NEXT_ID);
        setIsApplyFiltersSelected(DEFAULT_SELECTED);
    };

    const onApplyFiltersChange = (value: boolean) => {
        if (value && filters.length === 1) {
            crmEnrichmentTrackingService.filters.trackAdd(1, crmProvider);
        } else {
            crmEnrichmentTrackingService.filters.trackSelectAllEntities(crmProvider);
        }

        setIsApplyFiltersSelected(value);
    };

    return {
        isApplyFiltersSelected,
        crmFieldOptions,
        filters,
        onApplyFiltersChange,
        onAddFilter,
        onDeleteFilter,
        onFilterChange,
        onCrmFieldFilterChange,
        onResetFilters,
        setFiltersFromInitial,
    };
};

export default useCrmEnrichmentJobFilters;
